import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { throttle } from "lodash-es";

import { auth0Provider } from "@/common/auth0Provider";
import { LocalStorageKey } from "@/common/constants/localStorage";
import { ROUTE_PATH } from "@/common/constants/routing";
import { LocalStorageHelper } from "@/common/helpers/storage/localStorage";
import { UrlHelper } from "@/common/helpers/url";
import { tenantService } from "@/common/services/tenant";

import {
  AuthorizationProfileDto,
  CompanyIdentifierValidateResultDto,
  PasswordPolicyDto,
  PasswordValidateResultDto,
  RegisterCompleteDto,
  RegisterDto,
  RegisterResultDto,
  SendEmailVerificationTokenDto,
  ValidateCompanyIdentifierDto,
  ValidateEmailVerificationTokenDto,
  ValidatePasswordDto,
} from "../../core/api/generated/v1";
import { apiClient } from "./../../core/api/ApiClient";
import { AppDispatch, AppRootState, AppThunkAction } from "./../index";

export interface SavedCompanyRegistrationInfo {
  companyId: string;
  userId?: string | null;
  email: string;
}

export interface AuthState {
  loading: {
    getAuthorizationProfile?: boolean;
  };
  /** undefined - means initializing/loading */
  isAuthenticated?: boolean;
  authorizationProfile?: AuthorizationProfileDto;
  savedCompanyRegistrationInfo?: SavedCompanyRegistrationInfo;
}

const savedCompanyRegistrationInfoCached =
  LocalStorageHelper.getTypedJsonItem<SavedCompanyRegistrationInfo>(
    LocalStorageKey.savedCompanyRegistrationInfo,
  );

const initialState: AuthState = {
  loading: {},
  isAuthenticated: undefined,
  authorizationProfile: undefined,
  savedCompanyRegistrationInfo: savedCompanyRegistrationInfoCached || undefined,
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<AuthState["loading"]>) => {
      state.loading = {
        ...state.loading,
        ...action.payload,
      };
    },
    _authenticateStarted: () => {},
    _authenticateSucceeded: (state) => {
      state.isAuthenticated = true;
    },
    _authenticateFailed: (state) => {
      state.isAuthenticated = false;
    },

    _authorizationProfileFetched: (state, action: PayloadAction<AuthorizationProfileDto>) => {
      state.authorizationProfile = action.payload;
    },

    _userDataFetched: (state, action) => {},

    _tokenRefreshSucceeded: (state, action) => {},
    _tokenRefreshFailed: () => {},
    _tokenRevokeSucceeded: () => {},
    _initialJwtRefresh: () => {},

    _logoutSucceeded: (state) => {
      state.isAuthenticated = false;
    },

    _registerCompanyInfoSucceeded: (state, action: PayloadAction<RegisterResultDto>) => {
      state.savedCompanyRegistrationInfo = {
        companyId: action.payload.companyId!,
        userId: action.payload.userId,
        email: action.payload.email!,
      };
      LocalStorageHelper.setJsonItem(LocalStorageKey.savedCompanyRegistrationInfo, action.payload);
    },
    _completeRegistrationSucceeded: (state) => {
      state.savedCompanyRegistrationInfo = undefined;
      LocalStorageHelper.removeItem(LocalStorageKey.savedCompanyRegistrationInfo);
    },
    resetSavedCompanyRegistrationInfo: (state) => {
      state.savedCompanyRegistrationInfo = undefined;
      LocalStorageHelper.removeItem(LocalStorageKey.savedCompanyRegistrationInfo);
    },
  },
});

export const {
  setLoading,
  _authenticateSucceeded,
  _authenticateFailed,
  _tokenRefreshSucceeded,
  _tokenRefreshFailed,
  _tokenRevokeSucceeded,
  _initialJwtRefresh,
  _userDataFetched,
  _logoutSucceeded,

  _authorizationProfileFetched,

  _registerCompanyInfoSucceeded,
  _completeRegistrationSucceeded,
  resetSavedCompanyRegistrationInfo,
} = authSlice.actions;

export const authReducer = authSlice.reducer;

// actions

export const logout = (): AppThunkAction<Promise<void>> => async (dispatch) => {
  const auth0 = auth0Provider.getAuth0Client();

  const loggedOutPath = ROUTE_PATH.AUTH_SIGNED_OUT;
  let returnToUrl = UrlHelper.updateUrlPathname(`${window.location.origin}`, loggedOutPath);

  if (tenantService.strategy === "basepath") {
    // const tenantInfo = tenantService.resolveTenant(undefined, true);
    // Auth0 doesn't support wildcards in pathname, so we must use the same pathname every time,
    // and handle redirect to desired pathname after logout manually
    returnToUrl = `${window.location.origin}/${tenantService.defaultTenantIdentifier}`;
    returnToUrl = UrlHelper.updateUrlPathname(
      `${window.location.origin}`,
      `/${tenantService.defaultTenantIdentifier}${loggedOutPath}`,
    );
  }

  try {
    await auth0?.logout({
      logoutParams: {
        returnTo: returnToUrl,
      },
    });
    await dispatch(_logoutSucceeded());
  } catch (error) {
    console.error(error);
  }
};

const _getAuthorizationProfile = async (
  dispatch: AppDispatch,
  getState: () => AppRootState,
): Promise<AuthorizationProfileDto | undefined> => {
  // ensure no parallel requests
  const isLoading = getState().auth.loading.getAuthorizationProfile;
  if (isLoading) {
    return undefined;
  }

  dispatch(
    setLoading({
      getAuthorizationProfile: true,
    }),
  );

  try {
    const response = await apiClient.profileApi.apiV1ProfileAuthorizationGet({
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
    });
    dispatch(_authorizationProfileFetched(response.data));
    // authService.setAuthorizationState(response.data.authorizationState);
    return response.data;
  } finally {
    dispatch(
      setLoading({
        getAuthorizationProfile: false,
      }),
    );
  }
};
const _getAuthorizationProfileThrottle = throttle(_getAuthorizationProfile, 1000, {
  leading: true,
  trailing: false,
});

export const getAuthorizationProfile =
  (): AppThunkAction<Promise<AuthorizationProfileDto | undefined>> =>
  async (dispatch, getState) => {
    return _getAuthorizationProfile(dispatch, getState);
  };
export const getAuthorizationProfileThrottle =
  (): AppThunkAction<Promise<AuthorizationProfileDto | undefined>> =>
  async (dispatch, getState) => {
    return _getAuthorizationProfileThrottle(dispatch, getState);
  };

export const validateCompanyIdentifier =
  (
    validateCompanyIdentifierDto: ValidateCompanyIdentifierDto,
  ): AppThunkAction<Promise<CompanyIdentifierValidateResultDto>> =>
  async () => {
    const response = await apiClient.accountApi.apiV1AccountRegisterCompanyIdentifierValidatePost({
      validateCompanyIdentifierDto,
    });
    return response.data;
  };

export const createNewCompany =
  (
    ...args: Parameters<typeof apiClient.accountApi.apiV1AccountRegisterCompanyNewPost>
  ): AppThunkAction =>
  async () => {
    await apiClient.accountApi.apiV1AccountRegisterCompanyNewPost(...args);
  };

export const registerCompanyInfo =
  (registerDto: RegisterDto): AppThunkAction =>
  async (dispatch) => {
    const response = await apiClient.accountApi.apiV1AccountRegisterCompanyInfoPost({
      registerDto,
    });
    dispatch(_registerCompanyInfoSucceeded(response.data));
  };

export const sendEmailVerificationToken =
  (sendEmailVerificationTokenDto: SendEmailVerificationTokenDto): AppThunkAction =>
  async () => {
    await apiClient.accountApi.apiV1AccountVerificationEmailTokenSendPost({
      sendEmailVerificationTokenDto,
    });
  };

export const validateEmailVerificationToken =
  (validateEmailVerificationTokenDto: ValidateEmailVerificationTokenDto): AppThunkAction =>
  async () => {
    await apiClient.accountApi.apiV1AccountVerificationEmailTokenValidatePost({
      validateEmailVerificationTokenDto,
    });
  };

export const getPasswordPolicy = (): AppThunkAction<Promise<PasswordPolicyDto>> => async () => {
  const response = await apiClient.accountApi.apiV1AccountPasswordPolicyGet();
  return response.data;
};

export const validateAccountPassword =
  (validatePasswordDto: ValidatePasswordDto): AppThunkAction<Promise<PasswordValidateResultDto>> =>
  async () => {
    const response = await apiClient.accountApi.apiV1AccountPasswordValidatePost({
      validatePasswordDto,
    });
    return response.data as unknown as PasswordValidateResultDto;
  };

export const completeRegistration =
  (registerCompleteDto: RegisterCompleteDto): AppThunkAction =>
  async (dispatch) => {
    await apiClient.accountApi.apiV1AccountRegisterCompanyCompletePost({ registerCompleteDto });
    dispatch(_completeRegistrationSucceeded());
  };
